import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

// Simplified monthly-compounding RD: M = R × [((1+r)^n − 1) / r] × (1+r)
function rdFV(R, monthlyRate, n) {
  if (R <= 0 || n <= 0) return 0;
  if (monthlyRate === 0) return R * n;
  return (R * (Math.pow(1 + monthlyRate, n) - 1) / monthlyRate) * (1 + monthlyRate);
}

export default function RdCalculator() {
  const [monthly, setMonthly] = useState(5000);
  const [rate, setRate] = useState(7);
  const [tenureMonths, setTenureMonths] = useState(60);

  const { maturity, deposited, interest, depositedPct, interestPct, rows } = useMemo(() => {
    const R = Number(monthly) || 0;
    const r = (Number(rate) || 0) / 12 / 100;
    const n = Number(tenureMonths) || 0;
    const maturity = rdFV(R, r, n);
    const deposited = R * n;
    const interest = maturity - deposited;
    const depositedPct = maturity > 0 ? (deposited / maturity) * 100 : 0;
    const interestPct = 100 - depositedPct;

    const rows = [];
    const wholeYears = Math.floor(n / 12);
    for (let k = 1; k <= wholeYears; k++) {
      const nk = k * 12;
      const mk = rdFV(R, r, nk);
      const dk = R * nk;
      rows.push({
        year: k,
        primary: mk,
        segments: [
          { value: dk, className: "bg-ink", legendClassName: "bg-ink", label: "Deposited" },
          { value: Math.max(0, mk - dk), className: "bg-brass", legendClassName: "bg-brass", label: "Interest" },
        ],
        columns: [
          { value: formatINR(dk) },
          { value: formatINR(mk) },
          { value: formatINR(mk - dk), accent: true },
        ],
      });
    }
    if (n > wholeYears * 12) {
      rows.push({
        year: `${wholeYears}y ${n - wholeYears * 12}m`,
        primary: maturity,
        segments: [
          { value: deposited, className: "bg-ink", legendClassName: "bg-ink", label: "Deposited" },
          { value: Math.max(0, interest), className: "bg-brass", legendClassName: "bg-brass", label: "Interest" },
        ],
        columns: [
          { value: formatINR(deposited) },
          { value: formatINR(maturity) },
          { value: formatINR(interest), accent: true },
        ],
      });
    }
    return { maturity, deposited, interest, depositedPct, interestPct, rows };
  }, [monthly, rate, tenureMonths]);

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Monthly Deposit (₹)" value={monthly} onChange={setMonthly} min={0} testId="rd-monthly" />
          <NumField label="Interest Rate (% per annum)" value={rate} onChange={setRate} step={0.1} min={0} max={20} testId="rd-rate" />

          <label className="block">
            <div className="flex items-center justify-between mb-2">
              <span className="eyebrow">Tenure (months)</span>
              <span className="num text-ink text-[14px]">{tenureMonths} m</span>
            </div>
            <input
              type="range" min={6} max={120} value={tenureMonths}
              onChange={(e) => setTenureMonths(Number(e.target.value))}
              className="w-full accent-[#14A5A5]" data-testid="rd-tenure"
            />
            <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num">
              <span>6</span><span>120</span>
            </div>
          </label>
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="rd-result">
          <div className="eyebrow mb-2">Maturity Value</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={maturity} testId="rd-future-value" />
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-2 eyebrow mb-2">
              <span>Deposited vs. Interest</span>
            </div>
            <div className="w-full h-3 rounded-sm overflow-hidden border hairline flex">
              <div style={{ width: `${depositedPct}%` }} className="bg-ink transition-all duration-500" />
              <div style={{ width: `${interestPct}%` }} className="bg-brass transition-all duration-500" />
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-ink-soft">
              <span><span className="inline-block w-2 h-2 bg-ink mr-1.5" /> Deposited · {depositedPct.toFixed(0)}%</span>
              <span><span className="inline-block w-2 h-2 bg-brass mr-1.5" /> Interest · {interestPct.toFixed(0)}%</span>
            </div>
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Total Deposited" value={formatINR(deposited)} />
            <Row label="Total Interest" value={formatINR(interest)} accent />
            <Row label="Maturity Value" value={formatINR(maturity)} />
          </div>

          <p className="mt-6 text-[12px] text-ink-soft/85 leading-relaxed border-l-2 border-brass/50 pl-3">
            This uses simplified monthly compounding for illustration. Many Indian banks compound
            RD interest quarterly, which can produce a slightly different maturity value — check
            your bank's exact terms.
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <YearBreakdown
          rows={rows}
          primaryLabel="Value at end of each year"
          tableHeaders={["Year", "Amount Deposited to Date", "Value at End of Year", "Interest Earned to Date"]}
          testIdPrefix="rd"
        />
      )}

      <CalculatorActions
        testIdPrefix="rd"
        withStrategyCta
        buildPayload={() => ({
          type: "rd",
          title: "RD Calculator",
          one_liner: `an RD maturity calculation — ${formatINR(monthly)}/month at ${rate}% for ${tenureMonths} months`,
          inputs: [
            { label: "Monthly Deposit", value: formatINR(monthly) },
            { label: "Interest Rate", value: `${rate}% p.a.` },
            { label: "Tenure", value: `${tenureMonths} months` },
          ],
          hero: { label: "Maturity Value", value: formatINR(maturity) },
          summary: [
            { label: "Total Deposited", value: formatINR(deposited) },
            { label: "Total Interest", value: formatINR(interest), accent: true },
          ],
          row_headers: ["Year", "Deposited to Date", "Value at End of Year", "Interest to Date"],
          rows: rows.map((r) => [
            String(r.year), formatINR(r.segments[0].value),
            formatINR(r.primary), formatINR(r.segments[1].value),
          ]),
        })}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}
